import {
	ActivityType,
	type ConsentUpdatePayload,
	type UnsnoozePayload,
} from "@repo/db/client";
import { sortBy } from "@repo/utils/objectUtils";
import type {
	ActivityLogItem,
	LinkedTaskParameters,
	Task,
	TaskId,
} from "./db/schema";

/**
 * How the left pane orders referrals in the views staff work from: referrals
 * that have not gone out first, then by priority, then oldest first. This is
 * the rule coordinators already follow by hand — see
 * `specs/left-pane-work-queue-sort.md`.
 *
 * It has to run in memory. `QueryBuilder.order()` addresses top-level
 * attributes only, and neither key is one: send status is a join against the
 * activity log, and the referral date is nested inside `linkedTaskParameters`.
 */

/**
 * The fields the ordering reads. Structural rather than `Task` so tests can
 * build fixtures without the enrichment and tandem machinery.
 */
export type SortableTask = {
	id: TaskId;
	priority: Task["priority"];
	linkedTaskParameters: LinkedTaskParameters | undefined;
	lastActivityTimestamp: Date;
	createdAt: Date;
};

/**
 * Higher sorts first; anything unrecognised ranks with the unprioritized.
 *
 * Matched exactly rather than case-insensitively so that ordering and the
 * badge always agree: `getPriorityColor` in `LeftPane` switches on these same
 * literals, so a value this ranked but that did not match there would sort to
 * the top while rendering as unprioritized. Typed `string` because the column
 * is free text in Postgres and is cast unchecked on the way to the client.
 */
export function priorityRank(priority: string | undefined): number {
	switch (priority) {
		case "Urgent":
			return 3;
		case "High":
			return 2;
		case "Medium":
			return 1;
		default:
			return 0;
	}
}

/**
 * Builds a date from calendar components in the local timezone, matching the
 * `YYYY-MM-DD` handling in `packages/utils/src/codec.ts`. Returns undefined if
 * the components are not a real date, so `02/31/2025` is rejected rather than
 * rolling over into March.
 */
function localDate(year: number, month: number, day: number): Date | undefined {
	const date = new Date(year, month - 1, day);
	const isRealDate =
		date.getFullYear() === year &&
		date.getMonth() === month - 1 &&
		date.getDate() === day;
	return isRealDate ? date : undefined;
}

/**
 * Parses the referral date strings, whose format was never normalised across
 * EHRs: Azalea writes `YYYY-MM-DD`, Athena `MM-DD-YYYY`, ECW and the demo data
 * `MM/DD/YYYY`, and referrals created in the web app a full ISO timestamp.
 *
 * Every date-only value is built from local components, which is the whole
 * point of parsing these by hand. `new Date("2026-08-01")` is UTC midnight
 * while `new Date("08/01/2026")` is local midnight, so left to the engine the
 * two formats sit hours apart and same-day referrals order by the viewer's
 * timezone rather than by date.
 *
 * There is deliberately no `new Date(value)` fallback. An unrecognised string
 * reaching the engine is exactly the behaviour this exists to prevent; callers
 * fall back to a real timestamp on the task instead.
 */
export function parseReferralDateString(
	value: string | undefined,
): Date | undefined {
	const trimmed = value?.trim();
	if (!trimmed) {
		return undefined;
	}

	// An ISO string carries a time, so the instant it names is meaningful.
	if (/^\d{4}-\d{2}-\d{2}[T ]/.test(trimmed)) {
		const parsed = new Date(trimmed);
		return Number.isNaN(parsed.getTime()) ? undefined : parsed;
	}

	const isoDate = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
	if (isoDate) {
		return localDate(
			Number(isoDate[1]),
			Number(isoDate[2]),
			Number(isoDate[3]),
		);
	}

	// Month-first, with either separator and inconsistent padding.
	const usDate = /^(\d{1,2})[/-](\d{1,2})[/-](\d{2}|\d{4})$/.exec(trimmed);
	if (usDate) {
		const rawYear = Number(usDate[3]);
		const year =
			usDate[3]!.length === 2
				? rawYear < 70
					? 2000 + rawYear
					: 1900 + rawYear
				: rawYear;
		return localDate(year, Number(usDate[1]), Number(usDate[2]));
	}

	return undefined;
}

/**
 * The referral date, wherever this EHR's parameters happen to keep it.
 *
 * An if-chain rather than a switch because the legacy discriminant is optional
 * — rows written before `type` existed carry no discriminant at all, so legacy
 * is reached by exclusion. Falsy rather than `=== undefined` because the value
 * arrives from the server as null.
 */
function referralDateString(
	params: LinkedTaskParameters | undefined,
): string | undefined {
	if (!params) {
		return undefined;
	}
	if (params.type === "azalea") {
		return params.referredToDate;
	}
	if (params.type === "leadingreach") {
		// LeadingReach parameters carry no date.
		return undefined;
	}
	return params.referralDate;
}

/**
 * The date a referral sorts by, falling back to when the task was created for
 * referrals that carry no usable date. Total, so callers need no null branch.
 */
export function referralSortDate(task: SortableTask): Date {
	return (
		parseReferralDateString(referralDateString(task.linkedTaskParameters)) ??
		task.createdAt
	);
}

/**
 * Orders the left pane by the working rule. Returns a new array.
 *
 * `referralSentTaskIds` is read only to order — nothing is dropped here, since
 * whether sent referrals belong in the list at all is the status filter's
 * decision and has already been made upstream.
 */
export function sortTasksForLeftPane<T extends SortableTask>(
	tasks: readonly T[],
	referralSentTaskIds: ReadonlySet<TaskId>,
): T[] {
	return sortBy(
		tasks,
		// Booleans compare as 0 and 1, so ascending puts not-yet-sent first.
		[(task) => referralSentTaskIds.has(task.id), "asc"],
		[(task) => priorityRank(task.priority), "desc"],
		[(task) => referralSortDate(task).getTime(), "asc"],
		// Ties fall back to the ordering this list used before the working rule.
		[(task) => task.lastActivityTimestamp.getTime(), "desc"],
	);
}

/** The activity-log fields that decide whether an item is someone's work. */
type ActivityForLastTouched = Pick<
	ActivityLogItem,
	"taskId" | "createdAt" | "activityType" | "payload"
>;

/**
 * Whether an activity item is staff or the agent working the referral, as
 * opposed to something that happened to it. Counting the latter would float
 * exactly the neglected referrals this sort exists to surface, which is why
 * the sort cannot use `lastActivityTimestamp`: the server bumps that on every
 * activity insert.
 *
 * Not work:
 * - A patient texting in.
 * - The unsnooze timer firing (`expired`) or an automatic retry. A coordinator
 *   unsnoozing by hand (`manual`) is work.
 * - A patient granting or declining consent. That reply writes a row to every
 *   open task for the patient, including referrals it had nothing to do with.
 *   The consent request we send (`requested`) is the agent's work.
 *
 * Agent errors count: the agent did act, and the error shows on the card.
 *
 * Dispatches on `activityType` rather than `payload.type`, as in `LeftPane`'s
 * sent query: the column is populated on every row. The payload casts follow
 * from it, since each type writes its own payload shape.
 */
export function isWorkActivity(item: ActivityForLastTouched): boolean {
	switch (item.activityType) {
		case ActivityType.SpruceMessageReceived:
			return false;
		case ActivityType.Unsnooze:
			return (item.payload as UnsnoozePayload).reason === "manual";
		case ActivityType.ConsentUpdate: {
			// Rows written before `status` existed carry only `consented`, read
			// the same way `ActivityLog` reads them.
			const { status, consented } = item.payload as ConsentUpdatePayload;
			return (status ?? (consented ? "granted" : "requested")) === "requested";
		}
		default:
			return true;
	}
}

/**
 * When each task was last touched: its newest work activity from staff or the
 * agent. Tasks with no such activity are absent; `sortTasksByLastTouched`
 * falls back to `createdAt` for them.
 */
export function lastTouchedByTask(
	items: ReadonlyArray<ActivityForLastTouched>,
): Map<TaskId, Date> {
	const lastTouched = new Map<TaskId, Date>();
	for (const item of items) {
		if (!isWorkActivity(item)) {
			continue;
		}
		const current = lastTouched.get(item.taskId);
		if (!current || item.createdAt > current) {
			lastTouched.set(item.taskId, item.createdAt);
		}
	}
	return lastTouched;
}

/** When a task was last touched, or when it was created if never. */
export function lastTouchedAt(
	task: Pick<SortableTask, "id" | "createdAt">,
	lastTouched: ReadonlyMap<TaskId, Date>,
): Date {
	return lastTouched.get(task.id) ?? task.createdAt;
}

/**
 * Least recently touched first, so staff can find what nobody has worked on.
 * Returns a new array.
 */
export function sortTasksByLastTouched<T extends SortableTask>(
	tasks: readonly T[],
	lastTouched: ReadonlyMap<TaskId, Date>,
): T[] {
	return sortBy(
		tasks,
		[(task) => lastTouchedAt(task, lastTouched).getTime(), "asc"],
		// Same final key as the other sorts, so tasks from one bulk pull that
		// were never worked do not fall in whatever order the store returned.
		[(task) => task.lastActivityTimestamp.getTime(), "desc"],
	);
}

/**
 * Oldest referral date first, regardless of send status or priority. The
 * working rule already ends on this key; this makes it the only one.
 * Returns a new array.
 */
export function sortTasksByReferralDate<T extends SortableTask>(
	tasks: readonly T[],
): T[] {
	return sortBy(
		tasks,
		[(task) => referralSortDate(task).getTime(), "asc"],
		[(task) => task.lastActivityTimestamp.getTime(), "desc"],
	);
}

/**
 * The orders staff can pick from the sort menu. `recommended` is each view's
 * own order: the working rule where staff pick up work, most recent activity
 * elsewhere.
 */
export type TaskSortMode = "recommended" | "longestIdle" | "oldestReferral";
